import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, ScanLine, CheckCircle2, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, PageTransition } from "@/components/Reveal";
import { trackButton } from "@/lib/analytics";
import { Seo } from "@/components/Seo";

const defaultHighlights = [
  { icon: ScanLine, title: "Captures", desc: "Reads any claim form, scan, or fax — even handwriting." },
  { icon: CheckCircle2, title: "Validates", desc: "Checks every field with confidence scoring and payer edits." },
  { icon: Workflow, title: "Organizes", desc: "Delivers clean, structured, integration-ready data instantly." },
];

/**
 * Reusable demo/watch page template.
 * Props: seo {title, description, keywords, canonical}, eyebrow, heading,
 * intro, videoId, videoTitle, highlights, source (analytics)
 */
export const DemoPage = ({
  seo,
  eyebrow = "See it in action",
  heading = "Demo",
  intro,
  videoId,
  videoTitle = "Hewto.ai demo",
  highlights = defaultHighlights,
  source = "demo_page",
}) => {
  const navigate = useNavigate();
  return (
    <PageTransition>
      <Seo {...seo} />
      {/* HEADER */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-slate-50">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 text-center sm:px-8 sm:py-24">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-100 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-600">
              <Sparkles className="h-4 w-4" /> {eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {heading}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">{intro}</p>
          </Reveal>
        </div>
      </section>

      {/* VIDEO */}
      <section className="mx-auto max-w-5xl px-5 py-20 sm:px-8">
        <Reveal>
          <div
            data-testid="demo-video"
            className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-2xl shadow-brand/10"
          >
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                title={videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {highlights.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <h.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{h.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{h.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-slate-950 px-8 py-16 text-center sm:px-16">
            <div className="absolute inset-0 bg-grid-dark opacity-40" aria-hidden="true" />
            <div
              className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-brand/30 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative mx-auto max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                See Hewto.ai on your documents
              </h2>
              <p className="mt-3 text-lg text-slate-300">
                Book a personalized demo and our team will walk you through it.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Button
                  data-testid="demo-contact-btn"
                  onClick={() => {
                    trackButton("Book Demo", { source });
                    navigate("/contact-sales");
                  }}
                  className="group rounded-full bg-brand px-6 py-6 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-600"
                >
                  Contact Sales
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </PageTransition>
  );
};

export default DemoPage;
